import { Payment } from '@models/account.model';
import { TranslateService } from '@ngx-translate/core';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DateTime } from 'luxon/src/luxon';

const myGCLogo = 'assets/public/images/myGCLogo_menu-white.png';

export interface PdfHeaderOptions {
  title: string;
  logo?: string;
  providerName?: string;
  doctor?: string;
  accountNumber?: string;
  marginX?: number;
  marginY?: number;
}

function hexToRgb(hex: string): [number, number, number] {
  hex = hex.trim().replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((x) => x + x)
      .join('');
  }
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b];
}

function resolveCssColor(varName: string): [number, number, number] {
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return hexToRgb(value);
}

const colors = {
  white: resolveCssColor('--white'),
  primary: resolveCssColor('--primary-color'),
  accent: resolveCssColor('--accent-color'),
  primaryLighter: resolveCssColor('--primary-lighter-color'),
  warning: resolveCssColor('--warning-orange'),
  black: resolveCssColor('--black'),
};

export function addPdfHeader(pdf: jsPDF, opts: PdfHeaderOptions): number {
  const { title, logo, providerName, doctor, accountNumber, marginX = 12, marginY = 12 } = opts;

  const pageWidth = pdf.internal.pageSize.getWidth();
  const rectHeight = 50;
  const rectWidth = pageWidth - marginX * 2;

  pdf.setFillColor(...colors.white);
  pdf.setDrawColor(...colors.primaryLighter);
  pdf.setLineWidth(0.5);
  pdf.roundedRect(marginX, marginY, rectWidth, rectHeight, 5, 5, 'FD');

  const headerHeight = 20;
  pdf.setFillColor(...colors.primary);
  pdf.roundedRect(marginX, marginY, rectWidth, headerHeight, 5, 5, 'FD');

  if (logo) {
    pdf.addImage(logo, 'PNG', marginX + 5, marginY + 5, 30, 10);
  }

  pdf.setTextColor(...colors.white);
  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  pdf.text(title, pageWidth / 2, marginY + 11, { align: 'center' });

  let y = marginY + 25;

  const circleX = 25;
  const circleY = y + 10;
  pdf.setFillColor(...colors.accent);
  pdf.circle(circleX, circleY, 7, 'F');

  pdf.setTextColor(...colors.black);
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'bold');
  pdf.text((providerName?.charAt(0) || '').toUpperCase(), circleX, circleY + 1.5, { align: 'center' });

  pdf.setFont(undefined, 'bold');
  pdf.text(providerName || '', 35, y + 8);
  pdf.setFont(undefined, 'normal');
  pdf.text(doctor || '', 35, y + 12);
  pdf.text(accountNumber || '', 35, y + 16);

  return y + 30;
}

export function downloadSummaryOfPayments(payment: Payment, translate: TranslateService, timestamp?: DateTime) {
  const pdf = new jsPDF('p', 'mm', 'a4');

  const pageWidth = 210;
  const pageHeight = 297;
  const marginX = 12;
  const marginY = 12;

  const rectWidth = pageWidth - marginX * 2;

  const treatingDoctors = () => {
    const doctors = payment.invoices
      ?.map((invoice) => invoice.treating_doc)
      .filter((doc, index, self) => doc && self.indexOf(doc) === index);
    return doctors?.join(', ') || translate.instant('na');
  };

  let y = addPdfHeader(pdf, {
    title: translate.instant('payment_summary_title'),
    logo: myGCLogo,
    providerName: payment.servicesProviderName,
    doctor: treatingDoctors(),
    accountNumber: payment.accountNumber,
    marginX,
    marginY,
  });

  const paidLabel = translate.instant('paid_total');
  const addNewPage = () => {
    pdf.addPage();
    y = marginY;
  };
  const paidDate = payment.paidDate ? payment.paidDate.toFormat('yyyy-MM-dd HH:mm') : timestamp?.toFormat('yyyy-MM-dd HH:mm');

  const paymentDetailsRaw = [
    [translate.instant('payment_date'), paidDate],
    [translate.instant('peachpay_checkout_id'), payment?.peachpayCheckoutId || null],
    [translate.instant('goodx_payment_link_id'), payment?.merchantTransactionId || null],
    [translate.instant('card_brand'), payment?.paymentBrand || null],
    [translate.instant('card_last_4_digits'), payment?.cardLast4Digits || null],
  ];

  const paymentDetails = paymentDetailsRaw.filter(([_, value]) => value !== null && value !== '');

  if (paymentDetails.length > 0) {
    pdf.setFontSize(13);
    pdf.setFont(undefined, 'bold');
    pdf.text(translate.instant('payment_details'), marginX, y + 8);
    /* eslint-disable @typescript-eslint/naming-convention */
    autoTable(pdf, {
      startY: y + 12,
      theme: 'grid',
      head: [],
      body: paymentDetails,
      styles: {
        fontSize: 11,
        cellPadding: 2,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { cellWidth: 'auto' },
      },
      didDrawPage: (data) => {
        y = data.cursor.y + 10;
      },
    });
  }
  /* eslint-enable @typescript-eslint/naming-convention */
  if (payment.invoices?.length) {
    pdf.setFontSize(13);
    pdf.setFont(undefined, 'bold');
    pdf.text(translate.instant('paid_invoices'), marginX, y);

    y += 5;

    for (const inv of payment.invoices) {
      const patient = payment.patients?.find((p) => p.patient_dbid === inv.patient_dbid);
      const patientName = `${patient?.patient_name || ''} ${patient?.patient_surname || ''}`.trim() || translate.instant('not_available');
      const invoiceData = [
        [translate.instant('invoice_number'), inv.invoice_nr || translate.instant('not_available')],
        [translate.instant('patient'), patientName],
        [translate.instant('treatment_date'), inv.treatment_date || translate.instant('not_available')],
        [translate.instant('treating_doctor'), inv.treating_doc || translate.instant('not_available')],
        [paidLabel, `R${Number(inv.total).toFixed(2) || '0,00'}`],
      ];

      /* eslint-disable @typescript-eslint/naming-convention */
      autoTable(pdf, {
        startY: y,
        theme: 'grid',
        head: [],
        body: invoiceData,
        styles: {
          fontSize: 11,
          cellPadding: 2,
        },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 50 },
          1: { cellWidth: 'auto' },
        },
        didDrawPage: (data) => {
          y = data.cursor.y + 5;
        },
      });
      /* eslint-enable @typescript-eslint/naming-convention */
      y = ((pdf as any).lastAutoTable?.finalY || y) + 5;
    }
  } else {
    pdf.setFont(undefined, 'normal');
    pdf.text(translate.instant('no_invoices_available'), marginX, y);
    y += 10;
  }

  if (y + 15 > pageHeight - marginY) addNewPage();
  const bannerHeight = 12;
  const bannerY = pageHeight - marginY - 15 - bannerHeight - 3;
  pdf.setFillColor(...colors.warning);
  pdf.setDrawColor(...colors.warning);
  pdf.roundedRect(marginX, bannerY, rectWidth, bannerHeight, 3, 3, 'F');

  pdf.setTextColor(...colors.white);
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'bold');

  pdf.setFont(undefined, 'normal');
  pdf.text(translate.instant('bank_reference_notice'), marginX + 4, bannerY + 7);
  pdf.setFillColor(...colors.primary);
  pdf.roundedRect(marginX, pageHeight - marginY - 15, rectWidth, 15, 5, 5, 'F');
  pdf.setTextColor(...colors.white);
  pdf.setFont(undefined, 'bold');
  pdf.text(`${paidLabel}: R${payment.paymentAmount.toFixed(2) || '0,00'}`, marginX + 5, pageHeight - marginY - 6);
  pdf.text(paidDate, pageWidth - marginX - 35, pageHeight - marginY - 6);

  pdf.save(`payment-summary-${payment.accountNumber || 'unknown'}.pdf`);
}
